/**
 * Re-encodes a segment of a video using MediaRecorder + captureStream().
 * Used for both trimming (VideoEditorModal) and background compression
 * (CreateDroolSheet) to ensure uploads stay within Supabase's size limit.
 *
 * Output bitrate is capped at 4 Mbps. For a 30s clip that's ~15 MB —
 * comfortably within the 25 MB upload limit.
 *
 * MP4/H264 is preferred over WebM because it plays on all browsers including
 * Safari and iOS. WebM (VP9/VP8) is used only as a fallback.
 */

export function getSupportedMimeType(): string {
  const types = [
    'video/mp4;codecs=h264,aac', // best: universal — Chrome, Firefox, Safari, iOS, Android
    'video/mp4;codecs=h264',
    'video/mp4',
    'video/webm;codecs=vp9',     // fallback: Chrome/Firefox only, not Safari
    'video/webm;codecs=vp8',
    'video/webm',
  ];
  return types.find((t) => MediaRecorder.isTypeSupported(t)) ?? '';
}

/**
 * Re-encodes a video from `trimStart` to `trimEnd` seconds at 4 Mbps.
 *
 * @param src        Blob URL of the source video (used only when sourceVideo is not provided)
 * @param trimStart  Start time in seconds
 * @param trimEnd    End time in seconds
 * @param onProgress Progress callback, receives a fraction 0–1
 * @param sourceVideo Optional existing <video> element to reuse. When provided the element
 *                   must already have its src loaded. This is important on iOS Safari where
 *                   video.play() only succeeds on elements with prior user-gesture trust —
 *                   passing the visible trim-preview element avoids autoplay blocks.
 */
export function reencode(
  src: string,
  trimStart: number,
  trimEnd: number,
  onProgress: (fraction: number) => void,
  sourceVideo?: HTMLVideoElement,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const usingExisting = !!sourceVideo;
    const video = sourceVideo ?? document.createElement('video');

    // Only configure + attach to DOM when we created the element ourselves
    if (!usingExisting) {
      video.muted = true;
      video.playsInline = true;
      video.style.cssText =
        'position:fixed;left:-9999px;top:0;width:320px;height:180px;pointer-events:none';
      document.body.appendChild(video);
    }

    const cleanup = () => {
      if (usingExisting) return; // caller owns this element — don't touch it
      try { video.pause(); } catch { /* ignore */ }
      video.src = '';
      video.load();
      if (video.parentNode) video.parentNode.removeChild(video);
    };

    video.onerror = () => { cleanup(); reject(new Error('Video failed to load')); };

    const setupRecording = () => {
      const { videoWidth: vw, videoHeight: vh } = video;

      const nativeCapture =
        (video as HTMLVideoElement & { captureStream?: (fps?: number) => MediaStream; mozCaptureStream?: (fps?: number) => MediaStream }).captureStream
        ?? (video as HTMLVideoElement & { mozCaptureStream?: (fps?: number) => MediaStream }).mozCaptureStream;

      let stream: MediaStream | null = null;
      if (nativeCapture) {
        try { stream = nativeCapture.call(video, 30); } catch { /* fall through to canvas */ }
      }

      let canvas: HTMLCanvasElement | null = null;
      let ctx: CanvasRenderingContext2D | null = null;
      let rafId = 0;

      if (!stream) {
        canvas = document.createElement('canvas');
        canvas.width = vw;
        canvas.height = vh;
        ctx = canvas.getContext('2d')!;
        stream = canvas.captureStream(30);
      }

      const mimeType = getSupportedMimeType();
      const opts: MediaRecorderOptions = { videoBitsPerSecond: 4_000_000 };
      if (mimeType) opts.mimeType = mimeType;

      let recorder: MediaRecorder;
      try {
        recorder = new MediaRecorder(stream, opts);
      } catch (e) {
        cleanup();
        reject(e);
        return;
      }

      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = () => {
        cleanup();
        resolve(new Blob(chunks, { type: recorder.mimeType || mimeType || 'video/mp4' }));
      };
      recorder.onerror = (e) => { cleanup(); reject(e); };

      const duration = Math.max(trimEnd - trimStart, 0.1);

      const drawLoop = () => {
        if (!ctx || !canvas) return;
        if (video.currentTime >= trimEnd) {
          cancelAnimationFrame(rafId);
          recorder.stop();
          video.pause();
          return;
        }
        ctx.drawImage(video, 0, 0, vw, vh);
        onProgress(Math.min(1, (video.currentTime - trimStart) / duration));
        rafId = requestAnimationFrame(drawLoop);
      };

      const onTimeUpdate = () => {
        if (video.currentTime >= trimEnd) {
          video.removeEventListener('timeupdate', onTimeUpdate);
          cancelAnimationFrame(rafId);
          if (recorder.state === 'recording') recorder.stop();
          video.pause();
        } else {
          onProgress(Math.min(1, (video.currentTime - trimStart) / duration));
        }
      };

      video.onseeked = () => {
        video.onseeked = null;
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (ctx && canvas) {
            ctx.drawImage(video, 0, 0, vw, vh);
          }
          recorder.start(200);
          video.addEventListener('timeupdate', onTimeUpdate);
          if (ctx && canvas) {
            video.play()
              .then(() => { rafId = requestAnimationFrame(drawLoop); })
              .catch((e) => { cleanup(); reject(e); });
          } else {
            video.play().catch((e) => { cleanup(); reject(e); });
          }
        }));
      };

      video.currentTime = trimStart;
    };

    // If reusing an existing element that already has metadata, set up recording immediately.
    // Otherwise wait for the metadata event (and load the src if we created the element).
    if (usingExisting && video.readyState >= 1 /* HAVE_METADATA */) {
      setupRecording();
    } else {
      video.onloadedmetadata = setupRecording;
      if (!usingExisting) {
        video.src = src;
        video.load();
      }
    }
  });
}
